/**
 * Genera un array di date (DD/MM/YYYY) per la settimana e l'anno indicati.
 * @param {number} [numeroSettimana] - Numero della settimana ISO (default: settimana corrente).
 * @param {number|null} [year=null] - Anno di riferimento opzionale (default: anno corrente).
 * @returns {string[]} Array di 7 stringhe rappresentanti le date formattate.
 */
export function impostaDateSettimana(numeroSettimana = getWeekNumber(new Date()), year = null) {
  // Se year è null o undefined, usa l'anno corrente
  const anno = year ?? new Date().getFullYear();
  
  // 1. Trova il lunedì della settimana (Logica corretta)
  const lunedi = new Date(anno, 0, 4 + (numeroSettimana - 1) * 7);
  const giorno = lunedi.getDay();
  const diff = giorno === 0 ? -6 : 1 - giorno;
  lunedi.setDate(lunedi.getDate() + diff);

  // 2. Genera l'array (7 giorni)
  const formattaData = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });

  return Array.from({ length: 7 }, (_, i) => {
    const data = new Date(lunedi);
    data.setDate(lunedi.getDate() + i);
    return formattaData.format(data);
  });
}

/**
 * Popola un insieme di elementi input basandosi su un array di dati.
 * @param {NodeList|Array<HTMLInputElement>} elemDom - Lista di elementi DOM (input) da popolare.
 * @param {Array<string|number>} days - Array contenente i valori da assegnare (es. date o nomi giorni).
 */
export function populateDays(elemDom, days) {
  elemDom.forEach((elem) => {
    // Verifichiamo che esista l'indice nel dataset per evitare errori
    if (elem.dataset.numDay !== undefined) {
      const index = parseInt(elem.dataset.numDay, 10);
      
      // Verifichiamo che l'indice sia valido nell'array days
      if (days[index] !== undefined) {
        elem.value = days[index];
      }
    }
  });
}

/**
 * Estrae l'anno e il numero della settimana da una chiave stringa (es. "2026_23") WeekAndYear.
 * @param {string} key - La chiave nel formato "anno_settimana".
 * @returns {{anno: number, numWeek: number}} Oggetto con anno e numero settimana convertiti in numeri.
 */
export function extractWAY(key) {
  const [anno, numWeek] = key.split('_').map(Number);
  return { anno, numWeek };
};

/**
 * Funzione di supporto per calcolare il numero della settimana (ISO-8601)
 */
export function getWeekNumber(d) {
  // Se 'd' è una stringa, la trasforma in Date. Se è già un Date, lo lascia intatto.
  const dataValida = typeof d === 'string' ? new Date(d) : d;

  // Da qui in poi usi dataValida al posto di d
  let copiaData = new Date(Date.UTC(dataValida.getFullYear(), dataValida.getMonth(), dataValida.getDate()));
  copiaData.setUTCDate(copiaData.getUTCDate() + 4 - (copiaData.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(copiaData.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((copiaData - yearStart) / 86400000) + 1) / 7);
  
  return weekNo;
}

/**
 * Calcola le date dei 7 giorni (da lunedì a domenica) di una specifica settimana dell'anno,
 * formattate in stile italiano (DD/MM/YYYY).
 * * Basato sullo standard ISO-8601, dove la prima settimana dell'anno è quella che 
 * contiene il primo giovedì dell'anno (o il 4 gennaio).
 *
 * @param {number} numeroSettimana - Il numero della settimana (es. da 1 a 53).
 * @param {number} anno - L'anno di riferimento a quattro cifre (es. 2026).
 * @returns {string[]} Un array di 7 stringhe contenente le date da lunedì a domenica nel formato "GG/MM/AAAA".
 * * @example
 * const date = calcolaDateSettimana(26, 2026);
 * // ritorna ["22/06/2026", "23/06/2026", ..., "28/06/2026"]
 */
export function calcolaDateSettimana(numeroSettimana, anno) {
  // 1. Calcola il lunedì della settimana richiesta
  // Usiamo il 4 gennaio come riferimento per la prima settimana (standard ISO)
  const inizioAnno = new Date(anno, 0, 4);
  const giorniAggiuntivi = (numeroSettimana - 1) * 7;
  
  // Trova il lunedì della settimana specificata
  const lunedi = new Date(anno, 0, 4 + giorniAggiuntivi);
  const giornoSettimana = lunedi.getDay(); 
  const differenza = giornoSettimana === 0 ? -6 : 1 - giornoSettimana;
  lunedi.setDate(lunedi.getDate() + differenza);

  // 2. Formattatore Italiano
  const formattaData = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return Array.from({ length: 7 }, (_, i) => {
    const data = new Date(lunedi);
    data.setDate(lunedi.getDate() + i);
    return formattaData.format(data);
  });
}

/**
 * Verifica la presenza di gare future per un determinato atleta e calcola quante settimane 
 * mancano all'evento rispetto alla settimana della scheda corrente.
 * Gestisce automaticamente lo scavalco dell'anno calcolando la differenza assoluta nel tempo.
 *
 * @param {number} week - Il numero della settimana della scheda corrente (es. 28).
 * @param {Object} payload - Il payload principale contenente i dati dell'applicazione.
 * @param {Object} payload.race - Contenitore dei dati relativi alle gare.
 * @param {Object} payload.race.lista - Oggetto che racchiude tutte le gare registrate (chiave: "YYYY_WW").
 * @param {string} idAtleta - L'ID univoco o la chiave dell'atleta da cercare tra gli iscritti delle gare.
 * @param {number|string|Date} [dataDiRiferimento=Date.now()] - Il punto di partenza temporale per la ricerca 
 * delle gare future. Può essere un timestamp, una stringa ISO o un oggetto Date (utile per calcoli predittivi su schede future).
 * @returns {null|void} Restituisce `null` se non vengono trovate gare future, altrimenti aggiorna il DOM e non restituisce nulla.
 */
export function checkRace(week, payload, idAtleta) {
  // 1. Estrai la lista delle gare in sicurezza
  const listaGare = payload?.race?.lista;
  if (!listaGare) return;
  
  // 2. Prendi le date delle gare dell'atleta e ORDINALE cronologicamente
  const dateGareAtleta = Object.values(listaGare)
    .filter(({ iscritti }) => iscritti && iscritti[idAtleta])
    .map(({ data }) => data)
    .sort((a, b) => Date.parse(a) - Date.parse(b));

  if (dateGareAtleta.length === 0) return null;

  // 3. Calcola il Lunedì della scheda corrente
  const annoAttuale = new Date().getFullYear();
  const lunediSchedaCorrente = getDateFromWeek(week, annoAttuale);
  const timestampScheda = lunediSchedaCorrente.getTime();

  // 4. Trova l'INDICE della prima gara utile (usiamo findIndex anziché find)
  const indiceGaraValida = dateGareAtleta.findIndex(dataGara => {
    const numWeekGara = getWeekNumber(dataGara);
    const annoGara = new Date(dataGara).getFullYear();
    const lunediGara = getDateFromWeek(numWeekGara, annoGara);
    return lunediGara.getTime() >= timestampScheda;
  });

  // Se tutte le gare in archivio sono nel passato rispetto alla scheda
  if (indiceGaraValida === -1) {
    const targetElem = document.getElementById('note-note');
    if (targetElem) targetElem.value = '';
    return;
  }

  const prossimaGaraValida = dateGareAtleta[indiceGaraValida];

  // 5. Calcoliamo la differenza per la gara corrente
  const numWeekGara = getWeekNumber(prossimaGaraValida);
  const annoGara = new Date(prossimaGaraValida).getFullYear();
  const lunediGara = getDateFromWeek(numWeekGara, annoGara);

  const diffInMillisecondi = lunediGara.getTime() - timestampScheda;
  const diff = Math.round(diffInMillisecondi / 604800000);

  // 6. Generiamo il messaggio basato sulla differenza reale
  let message = "";

  if (diff === 0) { 
    message = `🏆 Ci siamo! È la settimana della gara!`;
    
    // 🔍 CONTROLLO EXTRA: C'è un'altra gara subito dopo nello stesso array?
    const garaSuccessivaAQuellaCorrente = dateGareAtleta[indiceGaraValida + 1];
    
    if (garaSuccessivaAQuellaCorrente) {
      const numWeekProssima = getWeekNumber(garaSuccessivaAQuellaCorrente);
      const annoProssima = new Date(garaSuccessivaAQuellaCorrente).getFullYear();
      const lunediProssima = getDateFromWeek(numWeekProssima, annoProssima);
      
      const diffProssimaInMs = lunediProssima.getTime() - timestampScheda;
      const diffProssimaWeeks = Math.round(diffProssimaInMs / 604800000);
      
      // Aggiungiamo l'info al messaggio della settimana corrente
      if (diffProssimaWeeks === 1) {
        message += ` (E la prossima è già tra 1 settimana!)`;
      } else {
        message += ` (La successiva sarà tra ${diffProssimaWeeks} settimane.)`;
      }
    }
  }
  else if (diff === 1) { message = `🏆 Manca 1 settimana alla prossima gara.`; }
  else if (diff > 1) { message = `🏆 Mancano ${diff} settimane alla prossima gara.`; }
  else { message = `🏆 Nuovi obiettivi in arrivo!`; }
  
  const targetElem = document.getElementById('note-note');
  if (targetElem) targetElem.value = message;
  console.log(message)
}


function timeDiff(time, init = null ){
  const now = startTime ? new Date(init) : Date.now();
  const start = Date.parse(time);
  const diffInMs = start - now;

  // SICUREZZA: Se la gara è nel passato, restituisci tutto a zero
  if (diffInMs <= 0) {
    return { giorni: 0, ore: 0, minuti: 0, scaduto: true };
  }

  return {
    giorni: Math.floor(diffInMs / (1000 * 60 * 60 * 24)),
    ore: Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minuti: Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60)),
    scaduto: false
  };
}

/**
 * Calcola la data del lunedì di una specifica settimana dell'anno.
 * * @param {number} week - Il numero della settimana (es. 28)
 * @param {number} year - L'anno di riferimento (es. 2026)
 * @returns {Date} Oggetto Date corrispondente al lunedì di quella settimana alle 00:00:00
 */
export function getDateFromWeek(week, year) {
  // Crea una data impostata al 4 gennaio dell'anno (punto fermo per le settimane ISO)
  const ides = new Date(Date.UTC(year, 0, 4));
  // Trova il lunedì di quella settimana specifica
  const day = ides.getUTCDay() || 7;
  ides.setUTCDate(ides.getUTCDate() - day + 1 + (week - 1) * 7);
  return ides;
}